import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';
import { media } from '../../styles/theme';
import { listUsers } from '../../api/portalUserApi';
import { listAvailableTeachers } from '../../api/courseGroupTeacherApi';
import { getMyProfile } from '../../api/userProfileApi';
import { listSubjectsCached } from '../../api/subjectPortalApi';
import { YEAR_OPTIONS, DAY_OF_WEEK_OPTIONS } from '../../api/courseGroupPortalApi';
import * as S from './Management.styles';

// S.SplitLayout is built for a fixed 400px list + flexible detail pane and forces
// `height: calc(100vh - 200px)`. With only the table rendered (no day selected yet), that
// grid's lone child lands in its first 400px column — clipping every day past Tuesday and,
// since overflow-x:auto on a height-constrained box forces overflow-y:auto too, showing a
// vertical scrollbar instead of a usable horizontal one. This page needs its own layout: the
// table gets the remaining width and scrolls horizontally on its own terms, the detail panel
// is a fixed-width sidebar only when it's actually open.
const AvailabilityLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;

  ${media.tablet} {
    flex-direction: column;
  }
`;

const TableScrollArea = styled(S.TableContainer)`
  flex: 1;
  min-width: 0;
`;

const DetailPanelFixed = styled(S.DetailPanel)`
  width: 360px;
  flex-shrink: 0;
  max-height: 70vh;

  ${media.tablet} {
    width: 100%;
    max-height: none;
  }
`;

// A toggle-style pill, used for the grade/subject filters instead of a dropdown — clicking
// is faster to scan and to undo than opening and re-opening a <select>.
const FilterTag = styled.button`
  padding: 6px 14px;
  border: 1px solid ${props => (props.$active ? '#F1AF59' : '#d1d5db')};
  border-radius: 20px;
  background: ${props => (props.$active ? '#F1AF59' : 'white')};
  color: ${props => (props.$active ? 'white' : '#374151')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #F1AF59;
  }

  ${media.phone} {
    min-height: 36px;
  }
`;

const FilterTagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterSectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const FiltersPanel = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
`;

const DayHeaderButton = styled.button`
  width: 100%;
  padding: 8px 4px;
  border: none;
  border-radius: 6px;
  background: ${props => (props.$active ? '#F1AF59' : 'transparent')};
  color: ${props => (props.$active ? 'white' : '#111827')};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${props => (props.$active ? '#F1AF59' : '#f3f4f6')};
  }
`;

const DayCell = styled(S.Td)`
  text-align: left;
  vertical-align: top;
  font-size: 12px;
  color: ${props => (props.$empty ? '#d1d5db' : '#374151')};
  min-width: 110px;
`;

const TimeChip = styled.div`
  background: #fef3e2;
  color: #92631a;
  border-radius: 4px;
  padding: 2px 6px;
  margin-bottom: 4px;
  display: inline-block;
  white-space: nowrap;
`;

const UnfilledBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 11px;
  font-weight: 600;
`;

const DetailTeacherCard = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailTeacherName = styled.div`
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const DetailTimeRow = styled.div`
  font-size: 13px;
  color: #374151;
  margin-bottom: 4px;
`;

const DetailMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const VIEW_ALLOWED_ROLES = ['portal_developer', 'portal_system_admin', 'portal_director', 'portal_manager', 'portal_admin'];

const TeacherAvailability = () => {
  const auth = useAuth();
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));
  const canView = VIEW_ALLOWED_ROLES.some(r => portalGroups.includes(r));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState([]);
  const [unfilledOnly, setUnfilledOnly] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await getMyProfile();
        const myCampuses = profile?.campusIds?.length
          ? profile.campusIds
          : (profile?.campusId ? [profile.campusId] : []);

        const [allUsers, subjectList] = await Promise.all([
          listUsers(),
          listSubjectsCached()
        ]);
        setSubjects(subjectList);

        // Disabled accounts shouldn't be offered for scheduling; deleted ones are already
        // dropped server-side by listUsers(). listAvailableTeachers() has no status field of
        // its own, so cross-reference by id against the user list to apply that cut.
        const activeTeacherIds = new Set(
          allUsers
            .filter(u => u.status !== 'disabled' && (u.roles || []).includes('portal_teacher'))
            .map(u => u.id)
        );

        const perCampus = await Promise.all(
          (myCampuses.length ? myCampuses : [null]).map(campusId => listAvailableTeachers(campusId))
        );

        const merged = new Map();
        perCampus.flat().forEach(t => {
          if (activeTeacherIds.has(t.id)) merged.set(t.id, t);
        });

        setTeachers(
          Array.from(merged.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
      } catch (err) {
        console.error('Error loading teacher availability:', err);
        setError('Failed to load teacher availability. / 加载老师时段信息失败。');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [canView]);

  const subjectName = (id) => subjects.find(s => s.id === id)?.subjectName || id;
  // teachableGrades is stored in DynamoDB as a list of strings (e.g. "5"), while YEAR_OPTIONS
  // uses numeric values — compare as strings so "5" matches 5.
  const gradeLabel = (v) => YEAR_OPTIONS.find(o => String(o.value) === String(v))?.label || v;

  const toggleInList = (list, setList, value) => {
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const filteredTeachers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return teachers.filter(t => {
      if (q && !(t.name || t.email || '').toLowerCase().includes(q)) return false;
      if (gradeFilter.length && !gradeFilter.some(g => (t.teachableGrades || []).some(tg => String(tg) === String(g)))) return false;
      if (subjectFilter.length && !subjectFilter.some(s => (t.teachableSubjectIds || []).includes(s))) return false;
      if (unfilledOnly && (t.availability || []).length > 0) return false;
      return true;
    });
  }, [teachers, search, gradeFilter, subjectFilter, unfilledOnly]);

  const windowsForDay = (teacher, dayValue) => {
    return (teacher.availability || [])
      .filter(a => Number(a.dayOfWeek) === dayValue)
      .slice()
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  };

  const dayTeachers = useMemo(() => {
    if (!selectedDay) return [];
    return filteredTeachers
      .map(t => ({ teacher: t, windows: windowsForDay(t, selectedDay) }))
      .filter(entry => entry.windows.length > 0);
  }, [filteredTeachers, selectedDay]);

  if (!canView) {
    return (
      <S.Container>
        <S.EmptyState>You do not have permission to view this page. / 你没有权限查看此页面。</S.EmptyState>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Teacher Availability</S.Title>
          <S.Subtitle>
            Weekly working hours your teachers have put on their own profile — use it to see who
            is free before you assign a class. / 老师自己填写的每周可工作时段，排课前先看一眼人力情况。
          </S.Subtitle>
        </div>
      </S.Header>

      {error && <S.EmptyState>{error}</S.EmptyState>}

      <FiltersPanel>
        <S.FiltersBar>
          <S.SearchInput
            placeholder="Search teacher name / 搜索老师姓名..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <S.SmallButton
            color={unfilledOnly ? '#b91c1c' : '#9ca3af'}
            onClick={() => setUnfilledOnly(!unfilledOnly)}
          >
            {unfilledOnly ? 'Showing: Not filled only' : 'Show teachers with no hours set'}
          </S.SmallButton>
        </S.FiltersBar>

        <FilterSectionLabel>Teachable Grades / 可授课年级</FilterSectionLabel>
        <FilterTagGroup>
          {YEAR_OPTIONS.map(opt => (
            <FilterTag
              key={opt.value}
              $active={gradeFilter.includes(opt.value)}
              onClick={() => toggleInList(gradeFilter, setGradeFilter, opt.value)}
            >
              {opt.label}
            </FilterTag>
          ))}
        </FilterTagGroup>

        <FilterSectionLabel>Teachable Subjects / 可授课科目</FilterSectionLabel>
        <FilterTagGroup>
          {subjects.map(subject => (
            <FilterTag
              key={subject.id}
              $active={subjectFilter.includes(subject.id)}
              onClick={() => toggleInList(subjectFilter, setSubjectFilter, subject.id)}
            >
              {subject.subjectName}
            </FilterTag>
          ))}
        </FilterTagGroup>
      </FiltersPanel>

      {loading ? (
        <S.EmptyState>Loading... / 加载中...</S.EmptyState>
      ) : filteredTeachers.length === 0 ? (
        <S.EmptyState>No teachers match the current filters. / 没有符合当前筛选条件的老师。</S.EmptyState>
      ) : (
        <AvailabilityLayout>
          <TableScrollArea>
            <S.Table>
              <thead>
                <tr>
                  <S.Th>Teacher</S.Th>
                  {DAY_OF_WEEK_OPTIONS.map(day => (
                    <S.Th key={day.value}>
                      <DayHeaderButton
                        $active={selectedDay === day.value}
                        onClick={() => setSelectedDay(selectedDay === day.value ? null : day.value)}
                      >
                        {day.label.slice(0, 3)}
                      </DayHeaderButton>
                    </S.Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map(teacher => {
                  const neverFilled = (teacher.availability || []).length === 0;
                  return (
                    <tr key={teacher.id}>
                      <S.Td>
                        <S.UserName>{teacher.name || teacher.email}</S.UserName>
                        {neverFilled && <UnfilledBadge>Not filled</UnfilledBadge>}
                      </S.Td>
                      {DAY_OF_WEEK_OPTIONS.map(day => {
                        const windows = windowsForDay(teacher, day.value);
                        return (
                          <DayCell key={day.value} $empty={windows.length === 0}>
                            {windows.length === 0 ? (
                              '—'
                            ) : (
                              windows.map((w, idx) => (
                                <TimeChip key={idx}>{w.startTime}-{w.endTime}</TimeChip>
                              ))
                            )}
                          </DayCell>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </S.Table>
          </TableScrollArea>

          {selectedDay && (
            <DetailPanelFixed>
              <S.DetailHeader>
                <h3>{DAY_OF_WEEK_OPTIONS.find(d => d.value === selectedDay)?.label}</h3>
              </S.DetailHeader>
              {dayTeachers.length === 0 ? (
                <S.EmptyState>No one has free hours on this day yet. / 这天目前没有老师标记空档。</S.EmptyState>
              ) : (
                dayTeachers.map(({ teacher, windows }) => (
                  <DetailTeacherCard key={teacher.id}>
                    <DetailTeacherName>{teacher.name || teacher.email}</DetailTeacherName>
                    <DetailTimeRow>
                      {windows.map(w => `${w.startTime}-${w.endTime}`).join(', ')}
                    </DetailTimeRow>
                    <DetailMeta>
                      {(teacher.teachableGrades || []).map(gradeLabel).join(', ') || 'No grades set'}
                      {' · '}
                      {(teacher.teachableSubjectIds || []).map(subjectName).join(', ') || 'No subjects set'}
                    </DetailMeta>
                  </DetailTeacherCard>
                ))
              )}
            </DetailPanelFixed>
          )}
        </AvailabilityLayout>
      )}
    </S.Container>
  );
};

export default TeacherAvailability;
